import { Info } from 'lucide-react';
import { cn } from '@nepal-ecom/ui';

/**
 * Single source of truth for image dimensions across the platform.
 * Keep in sync with apps/vendor-dashboard/src/components/shared/ImageSpec.tsx.
 */
export const IMAGE_SPECS = {
  productPhoto: {
    label: 'Product photo',
    dimensions: '1200 × 1200 px',
    ratio: '1:1 (square)',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  storeBanner: {
    label: 'Store banner',
    dimensions: '1600 × 320 px',
    ratio: '5:1',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  storeLogo: {
    label: 'Store logo',
    dimensions: '512 × 512 px',
    ratio: '1:1 (transparent PNG)',
    maxSize: '500 KB',
    formats: 'PNG · SVG · WebP',
  },
  ogImage: {
    label: 'Social share (OG)',
    dimensions: '1200 × 630 px',
    ratio: '1.91:1',
    maxSize: '1 MB',
    formats: 'JPG · PNG',
  },
  platformLogo: {
    label: 'Platform logo',
    dimensions: '512 × 128 px',
    ratio: '4:1 (transparent PNG)',
    maxSize: '500 KB',
    formats: 'PNG · SVG',
  },
  favicon: {
    label: 'Favicon',
    dimensions: '512 × 512 px',
    ratio: '1:1',
    maxSize: '200 KB',
    formats: 'PNG · ICO',
  },
  promoBanner: {
    label: 'Home promo banner',
    dimensions: '1920 × 540 px',
    ratio: '32:9',
    maxSize: '2 MB',
    formats: 'JPG · PNG · WebP',
  },
  emailHeader: {
    label: 'Email header',
    dimensions: '600 × 200 px',
    ratio: '3:1',
    maxSize: '300 KB',
    formats: 'PNG · JPG',
  },
  vendorDocument: {
    label: 'Verification document',
    dimensions: 'Any',
    ratio: 'Any',
    maxSize: '5 MB',
    formats: 'PDF · JPG · PNG',
  },
} as const;

export type ImageSpecKey = keyof typeof IMAGE_SPECS;

interface ImageSpecProps {
  spec: ImageSpecKey;
  variant?: 'inline' | 'card';
  className?: string;
}

export function ImageSpec({ spec, variant = 'inline', className }: ImageSpecProps) {
  const s = IMAGE_SPECS[spec];

  if (variant === 'card') {
    return (
      <div className={cn('rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2.5', className)}>
        <div className="flex items-start gap-2">
          <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed text-slate-600">
            <p className="font-semibold text-slate-700">{s.label} · {s.dimensions}</p>
            <p>
              <span className="text-slate-500">Ratio</span> {s.ratio}
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="text-slate-500">Max</span> {s.maxSize}
              <span className="text-slate-300 mx-1.5">·</span>
              {s.formats}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[11px] text-slate-500 font-medium tabular-nums', className)}>
      <Info className="h-3 w-3 text-slate-400" />
      <span className="text-slate-700">{s.dimensions}</span>
      <span className="text-slate-300">·</span>
      <span>{s.ratio}</span>
      <span className="text-slate-300">·</span>
      <span>≤ {s.maxSize}</span>
      <span className="text-slate-300">·</span>
      <span>{s.formats}</span>
    </span>
  );
}
